import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, RescheduleForm } from "@/components/BookingBits";
import { CalendarDays } from "lucide-react";

export const PortalBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [msg, setMsg] = useState("");
  const [reschedOpen, setReschedOpen] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/cuddlers/me/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const act = async (b, action) => {
    if (action === "cancel" && !window.confirm("Cancel this session? The member will be fully refunded.")) return;
    setBusy(b.id); setMsg("");
    try {
      await api.post(`/bookings/${b.id}/${action}`);
      setMsg({ accept: "Session accepted — it's now on your calendar.", decline: "Request declined — the member has been refunded.", cancel: "Session cancelled and refunded." }[action]);
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const requests = bookings.filter((b) => b.status === "requested");
  const upcoming = bookings.filter((b) => b.status === "confirmed");
  const history = bookings.filter((b) => !["requested", "confirmed"].includes(b.status));

  const byDate = upcoming.reduce((acc, b) => { (acc[b.date] = acc[b.date] || []).push(b); return acc; }, {});

  const Row = ({ b, actions }) => (
    <div className="tt-card p-5" data-testid={`portal-booking-${b.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-[#F7ECEB]">{b.user_name || "Member"}</div>
          <div className="text-sm text-[#A89896] mt-1">{b.date} · {b.time_slot} · {b.session_type} · {b.duration_minutes}min</div>
          {b.notes && <div className="text-xs text-[#A89896] mt-2 italic">"{b.notes}"</div>}
          {b.status === "requested" && b.expires_at && (
            <div className="text-xs text-[#E58B5E] mt-2">Respond by {new Date(b.expires_at).toLocaleString()}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <StatusBadge status={b.status} />
          <div className="mt-2 text-sm text-[#A89896]">You earn <span className="text-[#F7ECEB] font-medium">${Number(b.provider_net ?? b.amount * 0.85).toFixed(2)}</span></div>
        </div>
      </div>
      {actions && (
        <div className="mt-4 border-t border-[#382A2A] pt-4 flex flex-wrap gap-2">
          {b.status === "requested" && (
            <>
              <button onClick={() => act(b, "accept")} disabled={busy === b.id} className="btn-primary text-sm" data-testid={`accept-${b.id}`}>Accept</button>
              <button onClick={() => act(b, "decline")} disabled={busy === b.id} className="btn-ghost text-sm" data-testid={`decline-${b.id}`}>Decline</button>
            </>
          )}
          <button onClick={() => setReschedOpen(reschedOpen === b.id ? null : b.id)} className="text-sm text-[#E58B5E] underline px-2" data-testid={`portal-reschedule-${b.id}`}>Reschedule</button>
          {b.status === "confirmed" && (
            <button onClick={() => act(b, "cancel")} disabled={busy === b.id} className="text-sm text-[#A89896] underline px-2" data-testid={`portal-cancel-${b.id}`}>Cancel</button>
          )}
        </div>
      )}
      {reschedOpen === b.id && <RescheduleForm booking={b} onDone={() => { setReschedOpen(null); setMsg("Rescheduled — the member has been notified."); load(); }} onCancel={() => setReschedOpen(null)} />}
    </div>
  );

  return (
    <div className="mt-8 space-y-10" data-testid="portal-bookings">
      {msg && <div className="text-sm text-[#E58B5E]" data-testid="portal-bookings-msg">{msg}</div>}

      <section>
        <div className="text-xs uppercase tracking-[0.2em] text-[#E58B5E] mb-4">Requests awaiting your response ({requests.length})</div>
        {requests.length === 0 ? <p className="text-sm text-[#A89896]">No pending requests.</p>
          : <div className="space-y-3">{requests.map((b) => <Row key={b.id} b={b} actions />)}</div>}
      </section>

      <section>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C93B3B] mb-4"><CalendarDays className="w-4 h-4" /> Upcoming calendar ({upcoming.length})</div>
        {upcoming.length === 0 ? <p className="text-sm text-[#A89896]">Nothing confirmed yet.</p> : (
          <div className="space-y-6">
            {Object.keys(byDate).sort().map((d) => (
              <div key={d}>
                <div className="font-serif text-xl text-[#F7ECEB] mb-3">{new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
                <div className="space-y-3">{byDate[d].sort((a, b) => a.time_slot.localeCompare(b.time_slot)).map((b) => <Row key={b.id} b={b} actions />)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {history.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A89896] mb-4">History</div>
          <div className="space-y-3">{history.slice(0, 30).map((b) => <Row key={b.id} b={b} />)}</div>
        </section>
      )}
    </div>
  );
};
